import { eventControlBar } from 'pages/tournament/tabs/eventsTab/renderDraws/eventControlBar/eventControlBar';
import { renderDrawView } from 'pages/tournament/tabs/eventsTab/renderDraws/renderDrawView';
import { drawControlBar } from 'pages/tournament/tabs/eventsTab/renderDraws/drawControlBar';
import { cleanupDrawPanel } from 'pages/tournament/tabs/eventsTab/cleanupDrawPanel';
import { headerSortElement } from '../common/sorters/headerSortElement';
import { mapParticipantResults } from './mapParticipantResults';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { destroyTable } from 'pages/tournament/destroyTable';
import { navigateToEvent } from '../common/navigateToEvent';
import { orderSorter } from '../common/sorters/orderSorter';
import { tournamentEngine } from 'services/factory/engine';
import { displayConfig } from 'config/displayConfig';
import { getStatsColumns } from './getStatsColumns';

// constants
import { DRAWS_VIEW, ROUNDS_STATS } from 'constants/tmxConstants';
import { t } from 'i18n';

type CreateStatsTableParams = {
  structureId: string;
  eventId: string;
  drawId: string;
};

type CreateStatsTableResult = {
  replaceTableData: (params?: { participantFilter?: string }) => void;
  table: any;
};

export async function createStatsTable({
  structureId,
  eventId,
  drawId,
}: CreateStatsTableParams): Promise<CreateStatsTableResult> {
  let participantFilter: string | undefined;
  let participantMap: Record<string, any>;
  let structure: any;
  let table: any;

  const getParticipantMap = (participants: any[]) =>
    (participants ?? []).reduce((map: Record<string, any>, participant: any) => {
      map[participant.participantId] = participant;
      return map;
    }, {});

  const getFilteredStructureResults = () => {
    const { participants, eventData } = tournamentEngine.getEventData({
      participantFilters: { eventIds: [eventId], positionedParticipants: true },
      participantsProfile: { withScaleValues: true },
      allParticipantResults: true,
      pressureRating: true,
      refreshResults: true,
      eventId,
    });
    const drawData = eventData?.drawsData?.find((data: any) => data.drawId === drawId);
    structure = drawData?.structures?.find((s: any) => s.structureId === structureId);

    if (!participantMap) participantMap = getParticipantMap(participants ?? []);
    // Tag each participant with its group structureName so the groupName
    // column can render it. (Used to feed Tabulator's groupBy header too,
    // but the group is now a sortable column instead of a header band.)
    const matchUps = structure?.roundMatchUps ? Object.values(structure.roundMatchUps).flat() : [];
    matchUps.forEach(({ sides, structureName }: any) => {
      sides?.forEach((side: any) => {
        if (side.participantId && participantMap[side.participantId]) {
          participantMap[side.participantId].groupName = structureName;
        }
      });
    });

    return (structure?.participantResults ?? []).filter((pResults: any) => {
      const participant = participantMap[pResults.participantId];
      return (
        !participantFilter || participant?.participantName?.toLowerCase().includes(participantFilter?.toLowerCase())
      );
    });
  };

  const participantResults = getFilteredStructureResults();
  const getTableData = () =>
    participantResults
      ?.map((participantInfo: any) => mapParticipantResults({ ...participantInfo, participantMap }))
      .sort((a: any, b: any) => orderSorter(a.order, b.order));

  const updateTableData = () =>
    getFilteredStructureResults()?.map((participantInfo: any) =>
      mapParticipantResults({ ...participantInfo, participantMap }),
    );
  const replaceTableData = (params?: { participantFilter?: string }) => {
    if (params?.participantFilter !== undefined) participantFilter = params.participantFilter;
    table.replaceData(updateTableData());
  };

  const data = getTableData();
  const columns = getStatsColumns();

  const render = (data: any[]) => {
    destroyTable({ anchorId: DRAWS_VIEW });
    const element = document.getElementById(DRAWS_VIEW);

    // Group is a column now (not a Tabulator groupBy header) so users can
    // sort all participants together by any stat. Default-sort by groupName
    // so the initial view mirrors the per-group reading order people are
    // used to.
    //
    // `fitDataStretch` sizes each column to its content and stretches the
    // last column to fill the table holder when there's extra width — and
    // crucially, when the column row is wider than the viewport (typical
    // on mobile), the tableholder scrolls horizontally instead of squeezing
    // every column to fit. The previous `fitColumns` + `responsiveLayout:
    // 'collapse'` pair forced everything into the viewport and folded the
    // rest into a +/- toggle row, which the other RR sub-views don't do.
    table = new Tabulator(element, {
      headerSortElement: headerSortElement([
        'averageVariation',
        'averagePressure',
        'participantName',
        'pressureOrder',
        'pointsResult',
        'gamesResult',
        'matchUpsPct',
        'setsResult',
        'pointsPct',
        'gamesPct',
        'setsPct',
        'groupName',
        'result',
        'order',
      ]),
      height: window.innerHeight * (displayConfig.get().tableHeightMultiplier ?? 0.85),
      placeholder: t('ui.noParticipants'),
      layout: 'fitDataStretch',
      reactiveData: true,
      index: 'matchUpId',
      initialSort: [{ column: 'groupName', dir: 'asc' }],
      columns,
      data,
    });
  };

  render(data);

  const callback = ({ refresh, view }: { refresh?: boolean; view?: string } = {}) => {
    cleanupDrawPanel();
    if (view) {
      navigateToEvent({ eventId, drawId, structureId, renderDraw: true, view });
    } else {
      renderDrawView({ eventId, drawId, structureId, redraw: refresh, roundsView: view });
    }
  };

  drawControlBar({ structure, drawId, existingView: ROUNDS_STATS, callback, updateDisplay: replaceTableData });
  eventControlBar({ eventId, drawId, structureId, updateDisplay: replaceTableData });

  return { table, replaceTableData };
}
